import { useEffect, useState, useMemo } from "react";
import {
  XMarkIcon,
  FunnelIcon,
  AdjustmentsHorizontalIcon
} from "@heroicons/react/24/outline";
import Pagination from "@mui/material/Pagination";
import { singleFilter } from "../../Components/Product/FilterData";
import Loader from "../../Components/Loader/Loader";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllProductByFilter,
  getAllProductsByBrand,
  getAllProductsByVendor
} from "../../State/Product/Action";
import ProductCard from "../../Components/Products/ProductCard";
import CheckboxGroup from "../FilterSections/CheckboxGroup";
import RadioGroup from "../FilterSections/RadioGroup";
import ActiveFilter from "../FilterSections/ActiveFilter";
import SortDropdown from "../FilterSections/ShortDropdown";
import { Stack } from "@mui/material";
import CategoryBannerSlider from "./CategoryBannerSlider";

export default function DesignerProducts() {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [selectedSort, setSelectedSort] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(5);
  const [activeFilters, setActiveFilters] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const param = useParams();
  const dispatch = useDispatch();
  const { products, publicCategory } = useSelector((store) => store);

  const searchParams = new URLSearchParams(location.search);
  const pageSize = 50;

  // Get filter values from URL
  const categoryValue = searchParams.get("category");
  const productCategoryValue = searchParams.get("subCategory");
  const brandValue = searchParams.get("brand");
  const colorValue = searchParams.get("color");
  const sizeValue = searchParams.get("size");
  const priceValue = searchParams.get("price");
  const discountValue = searchParams.get("discount");
  const sortValue = searchParams.get("sort");
  const pageNumber = Number(searchParams.get("page") || 1);
  const stockValue = searchParams.get("stock");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pageNumber]);

  // Extract unique colors, sizes, and brands
  const uniqueColors = useMemo(() => {
    const seen = new Set();
    return products?.products?.response
      ?.flatMap(
        (product) => product.variants?.map((variant) => variant.productColor.color) || []
      )
      .filter((color) => {
        if (!color || seen.has(color.name)) return false;
        seen.add(color.name);
        return true;
      });
  }, [products?.products?.response]);

  const uniqueSizes = useMemo(() => {
    const seen = new Set();
    return products?.products?.response
      ?.flatMap(
        (product) => product.variants?.map((variant) => variant.size) || []
      )
      .filter((size) => {
        if (!size || seen.has(size.name)) return false;
        seen.add(size.name);
        return true;
      });
  }, [products?.products?.response]);

  const uniqueProductCategories = useMemo(() => {
    const seen = new Set();
    return products?.products?.response
      ?.map((product) => product.productCategory)
      .filter((productCategory) => {
        if (!productCategory || seen.has(productCategory.name)) return false;
        seen.add(productCategory.name);
        return true;
      });
  }, [products?.products?.response]);

  const uniqueCategories = useMemo(() => {
    const seen = new Set();
    return products?.products?.response
      ?.map((product) => product.category)
      .filter((category) => {
        if (!category || seen.has(category.name)) return false;
        seen.add(category.name);
        return true;
      });
  }, [products?.products?.response]);

  // Handle pagination change
  const handlePaginationChange = (event, value) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("page", value);
    const query = searchParams.toString();
    navigate({ search: `?${query}` });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle sort change
  const handleSortChange = (sortValue) => {
    setSelectedSort(sortValue);
    setSortDropdownOpen(false);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("sort", sortValue);
    const query = searchParams.toString();
    navigate({ search: `?${query}` });
  };

  // Handle checkbox filter
  const handleCheckBoxFilter = (value, sectionId) => {
    const searchParams = new URLSearchParams(location.search);
    let filterValue = searchParams.getAll(sectionId);

    if (filterValue.length > 0 && filterValue[0].split(",").includes(value)) {
      filterValue = filterValue[0].split(",").filter((item) => item !== value);
      if (filterValue.length === 0) {
        searchParams.delete(sectionId);
      }
    } else {
      filterValue.push(value);
    }

    if (filterValue.length > 0) {
      searchParams.set(sectionId, filterValue.join(","));
    }
    const query = searchParams.toString();
    navigate({ search: `?${query}` });
  };

  // Handle radio filter
  const handleRadioFilter = (e, sectionId) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set(sectionId, e.target.value);
    const query = searchParams.toString();
    navigate({ search: `?${query}` });
  };

  // Reset all filters
  const handleResetFilters = () => {
    navigate({ search: "" }); // Removes all filters from URL
    setActiveFilters([]);
    setSelectedSort("asc");
  };

  useEffect(() => {
    dispatch({ type: "CLEAR_PRODUCTS" });
    if (param.designer) {
      dispatch(getAllProductsByBrand(param.designer));
    } else {
      dispatch(getAllProductsByVendor(param.vendor))
    }
  }, [dispatch, param.designer, param.vendor]);

  // Update active filters when URL params change
  useEffect(() => {
    const newActiveFilters = [];

    if (productCategoryValue) {
      productCategoryValue.split(",").forEach((productCategory) => {
        newActiveFilters.push({ type: "subCategory", value: productCategory });
      });
    }

    if (categoryValue) {
      categoryValue.split(",").forEach((category) => {
        newActiveFilters.push({ type: "category", value: category });
      });
    }

    if (colorValue) {
      colorValue.split(",").forEach((color) => {
        newActiveFilters.push({ type: "color", value: color });
      });
    }

    if (sizeValue) {
      sizeValue.split(",").forEach((size) => {
        newActiveFilters.push({ type: "size", value: size });
      });
    }

    if (priceValue) {
      newActiveFilters.push({ type: "price", value: priceValue });
    }

    if (discountValue) {
      newActiveFilters.push({ type: "discount", value: discountValue });
    }

    if (sortValue) {
      newActiveFilters.push({ type: "sort", value: sortValue });
    }

    if (stockValue) {
      newActiveFilters.push({ type: "stock", value: stockValue });
    }

    setActiveFilters(newActiveFilters);
  }, [colorValue, sizeValue, brandValue, priceValue, productCategoryValue, discountValue, sortValue, stockValue, categoryValue]);

  // Remove a specific filter
  const removeFilter = (type, value) => {
    const params = new URLSearchParams(searchParams.toString());

    if (type === "price") {
      params.delete("price");
    } else {
      const currentValues = params.get(type)?.split(",") || [];
      const newValues = currentValues.filter((v) => v !== value);

      if (newValues.length > 0) {
        params.set(type, newValues.join(","));
      } else {
        params.delete(type);
      }
    }
    const query = params.toString();
    navigate({ search: `?${query}` });
  };

  // Memoized requestData to avoid unnecessary re-renders
  const requestData = useMemo(() => {
    const [minPrice, maxPrice] = priceValue?.split("-").map(Number) || [
      0, 100000
    ];

    return {
      categories: categoryValue ?? [],
      productCategories: productCategoryValue ?? [],
      brands: param.designer ?? [],
      colors: colorValue ?? [],
      sizes: sizeValue ?? [],
      sort: sortValue || "asc",
      pageNumber: pageNumber - 1, // ✅ Spring page index starts from 0
      pageSize: pageSize,
      stock: stockValue || "in_stock",
      minPrice,
      maxPrice,
      discounts: discountValue ?? [],
      vendor: param.vendor ?? null,
    };
  }, [priceValue, categoryValue, productCategoryValue, param.designer, param.vendor, colorValue, sizeValue, sortValue, pageNumber, stockValue, discountValue]);

  // Apply filters and sorting
  useEffect(() => {
    setLoading(true);

    // Simulate API call with delay
    const timeoutId = setTimeout(() => {
      dispatch({ type: "CLEAR_PRODUCTS" });
      dispatch(getAllProductByFilter(requestData));
      // ✅ update totalPages from backend response
      setTotalPages(products?.filterProducts?.response?.totalPages || 1);
      setLoading(false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [dispatch, requestData, param.designer, param.vendor]);

  // ✅ Directly use backend response products
  const currentProducts =
    products?.filterProducts?.response?.products || [];


  return (
    <div className="bg-white pb-6">
      <div>
        {/* Mobile filter modal */}
        {mobileFiltersOpen && (
          <div className="fixed inset-0 z-40 flex lg:hidden ">
            <div
              className="fixed inset-0 bg-black bg-opacity-25"
              onClick={() => setMobileFiltersOpen(false)}
            />

            <div className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl">
              <div className="flex items-center justify-between px-4">
                <h2 className="text-lg font-medium text-gray-900">Filters</h2>
                <button
                  type="button"
                  className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
                  onClick={() => setMobileFiltersOpen(false)}
                >
                  <span className="sr-only">Close menu</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              {/* Mobile Filters */}
              <div className="mt-5 ms-5 pt-5 border-t border-gray-200">
                {/* Brand Filter */}
                <CheckboxGroup
                  title="Category"
                  type="category"
                  options={uniqueCategories}
                  onToggle={(val) => handleCheckBoxFilter(val, "category")}
                />

                <CheckboxGroup
                  title="Sub Category"
                  type="subCategory"
                  options={uniqueProductCategories}
                  onToggle={(val) => handleCheckBoxFilter(val, "subCategory")}
                />


                <CheckboxGroup
                  title="Color"
                  type="color"
                  options={uniqueColors}
                  onToggle={(val) => handleCheckBoxFilter(val, "color")}
                />

                <CheckboxGroup
                  title="Size"
                  type="size"
                  options={uniqueSizes}
                  onToggle={(val) => handleCheckBoxFilter(val, "size")}
                />

                {singleFilter?.map((section) => (
                  <RadioGroup
                    key={section.id}
                    title={section.name}
                    name={section.id}
                    options={section.options}
                    selectedValue={searchParams.get(section.id)}
                    onChange={(val) => handleRadioFilter({ target: { value: val } }, section.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        <main className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-4">
          <div className="flex items-baseline justify-between border-b border-gray-200 pb-4 pt-4 md:pt-6">
            <h3 className="text-lg sm:text-xl md:text-2xl text-gray-900 font-medium">
              {param.designer?.toUpperCase() || param.vendor?.toUpperCase()}'S COLLECTION
            </h3>

            <div className="flex items-center">
              {/* Sort dropdown */}
              <SortDropdown
                selectedSort={selectedSort}
                onChange={handleSortChange}
              />

              <button
                type="button"
                className="-m-2 ml-4 p-2 text-gray-400 hover:text-gray-500 sm:ml-6 lg:hidden"
                onClick={() => setMobileFiltersOpen(true)}
              >
                <span className="sr-only">Filters</span>
                <FunnelIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Active filters */}
          <ActiveFilter activeFilters={activeFilters} removeFilter={removeFilter} handleResetFilters={handleResetFilters} />

          <section aria-labelledby="products-heading" className="pb-20 pt-6">
            <h2 id="products-heading" className="sr-only">
              Products
            </h2>

            <div className="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-5">
              {/* Filters */}
              <div className="hidden lg:block">
                <div className="flex items-center justify-between py-3">
                  <h1 className="text-lg font-medium text-gray-800">Filters</h1>
                  <div
                    onClick={handleResetFilters}
                    className="text-sm text-red-300 hover:text-red-500 flex items-center cursor-pointer"
                  >
                    <AdjustmentsHorizontalIcon className="mr-1 h-5 w-5" />
                    Reset
                  </div>
                </div>

                <form>
                  {/*  Category Filter */}
                  <CheckboxGroup
                    title="Category"
                    type="category"
                    options={uniqueCategories}
                    onToggle={(val) => handleCheckBoxFilter(val, "category")}
                  />

                  <CheckboxGroup
                    title="Sub Category"
                    type="subCategory"
                    options={uniqueProductCategories}
                    onToggle={(val) => handleCheckBoxFilter(val, "subCategory")}
                  />


                  <CheckboxGroup
                    title="Color"
                    type="color"
                    options={uniqueColors}
                    onToggle={(val) => handleCheckBoxFilter(val, "color")}
                  />

                  <CheckboxGroup
                    title="Size"
                    type="size"
                    options={uniqueSizes}
                    onToggle={(val) => handleCheckBoxFilter(val, "size")}
                  />

                  {singleFilter?.map((section) => (
                    <RadioGroup
                      key={section.id}
                      title={section.name}
                      name={section.id}
                      options={section.options}
                      selectedValue={searchParams.get(section.id)}
                      onChange={(val) => handleRadioFilter({ target: { value: val } }, section.id)}
                    />
                  ))}
                </form>
              </div>

              {/* Product grid */}
              <div className="lg:col-span-4">
                {/* Banner Section - Full Width */}
                <CategoryBannerSlider categories={publicCategory?.categories?.response} />

                {products.loading ? (
                  <div className="flex justify-center items-center h-96">
                    <Loader />
                  </div>
                ) : currentProducts?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-y-6 gap-x-4 sm:grid-cols-3 lg:grid-cols-4 xl:gap-x-4">
                    {currentProducts?.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                ) : (
                  <div className="flex justify-center items-center h-96">
                    <div className="text-center">
                      <p className="text-gray-500 text-lg mb-4">
                        No products found
                      </p>
                      {activeFilters?.length > 0 && (
                        <button
                          onClick={handleResetFilters}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Clear all filters
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Pagination */}
          {currentProducts?.length > 0 && (
            <section className="w-full">
              <div className="flex justify-center mt-6">
                <Stack spacing={2}>
                  <Pagination
                    count={totalPages}
                    page={pageNumber}
                    onChange={handlePaginationChange}
                    color="primary"
                    variant="outlined"
                    shape="rounded"
                    className="pagination-responsive"
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": {
                        backgroundColor: "#ef4444",
                        color: "#fff",
                        border: "1px solid #ef4444",
                        "&:hover": {
                          backgroundColor: "#ef4444",
                        }
                      }
                    }}
                  />
                </Stack>
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}